/**
 * Ecran de devinette. Coquille React mince autour de GuessEngine : le canvas,
 * le pan/zoom et la revelation vivent hors de React.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import {
  DrawingFormatError,
  DEFAULT_GUESS_TIME_MS,
  decodeDrawing,
  scoreDrawer,
  scoreGuesser,
  type DrawZone,
  type Drawing,
} from '@retusch/shared';
import { GuessEngine, type GuessResult } from './GuessEngine.js';
import type { DevOptionsStore } from './devOptions.js';
import { TimerRing } from '../ui/TimerRing.js';
import { useT } from '../locales/useT.js';
import { PhaseTimer } from '../ui/PhaseTimer.js';
import { LiveScore } from '../ui/LiveScore.js';
import { observeSize } from './canvas/observeSize.js';

export interface GuessBoardProps {
  readonly image: HTMLImageElement;
  readonly zone: DrawZone;
  /** Dessin encode, exactement tel qu'il transiterait par le serveur. */
  readonly buffer: ArrayBuffer;
  readonly palette: readonly string[];
  readonly dev: DevOptionsStore;
  readonly onDone?: () => void;
  /** Horodatage serveur de fin de phase (multijoueur). */
  readonly endsAt?: number | null;
  /** Remonte le clic au serveur, qui reste seul juge du score. */
  readonly onGuess?: (x: number, y: number) => void;
  /**
   * Points renvoyes par le serveur. Le hit/miss est calcule localement pour un
   * retour immediat (le client connait la zone), mais les points affiches sont
   * toujours ceux du serveur.
   */
  readonly serverPoints?: { readonly guesser: number; readonly drawer: number } | null;
  /**
   * Duree de la phase, en ms. Vient de settings.guessTime en partie reelle ;
   * la sandbox retombe sur la valeur par defaut.
   */
  readonly guessTimeMs?: number;
  /** Mode de jeu. 'classic' laisse le rendu strictement inchange. */
}

export function GuessBoard({
  image,
  zone,
  buffer,
  palette,
  dev,
  onDone,
  endsAt = null,
  onGuess,
  serverPoints = null,
  guessTimeMs = DEFAULT_GUESS_TIME_MS,
}: GuessBoardProps): ReactElement {
  const t = useT();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const engineRef = useRef<GuessEngine | null>(null);

  const [result, setResult] = useState<GuessResult | null>(null);
  const [startedAt, setStartedAt] = useState(0);
  const onGuessRef = useRef(onGuess);
  onGuessRef.current = onGuess;

  // Le dessin arrive comme un blob opaque : le decodage peut echouer, et cet
  // ecran doit le dire au lieu de rendre un canvas vide.
  const decoded = useMemo<{ drawing: Drawing } | { error: string }>(() => {
    // Un joueur qui n'a rien rendu (deconnecte, ou qui n'a pas dessine) produit
    // un blob VIDE. Ce n'est pas une erreur : sa zone reste blanche, et c'est
    // en soi une information pour le devineur.
    if (buffer.byteLength === 0) return { drawing: [] };
    try {
      return { drawing: decodeDrawing(buffer) };
    } catch (error) {
      const message =
        error instanceof DrawingFormatError ? error.message : 'dessin illisible';
      return { error: message };
    }
  }, [buffer]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas === null || !('drawing' in decoded)) return;

    const engine = new GuessEngine(
      { canvas, image, zone, drawing: decoded.drawing, palette, dev },
      (outcome) => {
        setResult(outcome);
        if (outcome.point.x !== 0 || outcome.point.y !== 0) {
          onGuessRef.current?.(outcome.point.x, outcome.point.y);
        }
      },
    );
    engineRef.current = engine;
    engine.start();
    setStartedAt(performance.now());

    // La taille du canvas change aussi SANS evenement de fenetre (barre
    // d'outils qui grandit, panneau qui s'ouvre) : on observe la boite.
    const stopResize = observeSize(canvas, () => engine.resize());

    return () => {
      stopResize();
      engine.destroy();
      engineRef.current = null;
    };
  }, [decoded, image, zone, palette, dev]);

  const onTimeout = useCallback(() => engineRef.current?.timeout(), []);

  if ('error' in decoded) {
    return <p className="sandbox__status">{t('guessing.drawingUnreadable', { reason: decoded.error })}</p>;
  }

  // Les points du serveur priment des qu'ils arrivent ; en attendant on affiche
  // l'estimation locale, calculee avec la meme courbe.
  const guesserPoints =
    serverPoints?.guesser ?? (result === null ? null : scoreGuesser({ ...result, guessTimeMs }));
  const drawerPoints =
    serverPoints?.drawer ?? (result === null ? null : scoreDrawer({ ...result, guessTimeMs }));

  return (
    <div className="board">
      <canvas ref={canvasRef} className="board__canvas board__canvas--inspect" />

      <div className="board__hud">
        {result === null ? (
          <LiveScore startedAt={startedAt} frozen={false} guessTimeMs={guessTimeMs} />
        ) : (
          <span className="board__hint">{t('guessing.locked')}</span>
        )}
        {/* Pas d'indicateur de nettete : le tableau qui se precise EST
            l'indicateur, un pourcentage a cote ne fait que voler l'attention. */}
        <span className="board__timer">
          {endsAt === null ? (
          <TimerRing durationMs={guessTimeMs} running={result === null} onExpire={onTimeout} />
        ) : (
          // Meme raison qu'a la phase de dessin : une fois le clic verrouille on
          // attend les autres, et savoir combien de temps fait la difference
          // entre une attente et un blocage.
          <PhaseTimer
            endsAt={endsAt}
            durationMs={guessTimeMs}
            {...(result === null ? { onExpire: onTimeout } : {})}
          />
        )}
        </span>
      </div>

      {result !== null && (
        <div className="result">
          <p className="result__verdict" data-hit={result.found}>
            {result.found ? t('guessing.found') : t('guessing.missed')}
          </p>
          <p className="result__detail">
            {result.found
              ? t('guessing.inSeconds', { seconds: (result.elapsedMs / 1000).toFixed(1) })
              : Number.isFinite(result.distancePx)
                ? t('guessing.distance', { px: Math.round(result.distancePx) })
                : t('guessing.timeout')}
          </p>

          <div className="result__scores">
            <span>
              <span className="result__score-value">{guesserPoints}</span>
              <span className="result__score-label">{t('common.guesser')}</span>
            </span>
            <span>
              <span className="result__score-value">{drawerPoints}</span>
              <span className="result__score-label">{t('common.drawer')}</span>
            </span>
          </div>

          {onDone !== undefined && (
            <button type="button" className="button-primary" onClick={onDone}>
              {t('common.replay')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
